import React, {
  CSSProperties,
  FC,
  SyntheticEvent,
  useEffect,
  useState,
} from "react";
import {
  Combobox,
  makeStyles,
  Option,
  useId,
  Spinner,
} from "@fluentui/react-components";
import type {
  ComboboxProps,
  OptionOnSelectData,
  ComboboxOpenChangeData,
} from "@fluentui/react-components";

const useStyles = makeStyles({
  root: {
    display: "grid",
    gap: "2px",
    maxWidth: "max-content",
    minWidth: "min-content",
  },
  combobox: {
    minWidth: "200px",
  },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "40px",
  },
});

interface OptionType {
  label: string;
  value: string;
}

interface CustomDropDownProps extends Partial<ComboboxProps> {
  label?: string;
  optionsFetcher?: () => Promise<OptionType[]>;
  values?: OptionType[];
  selectedValues?: string[]; // ✅ NEW (controlled)
  onSelectionChange?: (selectedValues: string[]) => void;
  className?: string;
  style?: CSSProperties;
  multiSelect?: boolean;
}

const ASCDropDown: FC<CustomDropDownProps> = ({
  label,
  optionsFetcher,
  values,
  selectedValues: controlledSelectedValues,
  onSelectionChange,
  className,
  style,
  multiSelect = false,
  ...rest
}) => {
  const comboId = useId("combo-default");
  const styles = useStyles();

  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<OptionType[]>([]);
  const [internalSelected, setInternalSelected] = useState<string[]>([]);

  /* -------------------------------------------------
     Sync controlled values → internal state
  -------------------------------------------------- */
  useEffect(() => {
    if (Array.isArray(controlledSelectedValues)) {
      setInternalSelected(controlledSelectedValues);
    }
  }, [controlledSelectedValues]);

  const handleOptionSelect = (
    _: SyntheticEvent<HTMLElement>,
    data: OptionOnSelectData,
  ) => {
    const clickedOption = options.find((o) => o.label === data.optionValue);
    if (!clickedOption) return;

    const updated = internalSelected.includes(clickedOption.value)
      ? internalSelected.filter((v) => v !== clickedOption.value)
      : [...internalSelected, clickedOption.value];

    setInternalSelected(updated);
    onSelectionChange?.(updated);
  };

  const handleOpenChange = async (
    _: SyntheticEvent<HTMLElement>,
    data: ComboboxOpenChangeData,
  ) => {
    if (data.open && options.length === 0) {
      setLoading(true);
      try {
        if (optionsFetcher) {
          setOptions(await optionsFetcher());
        } else if (values) {
          setOptions(values);
        }
      } catch (err) {
        console.error("Failed to fetch options", err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const selectedLabels = options
    .filter((o) => internalSelected.includes(o.value))
    .map((o) => o.label);

  return (
    <div className={`${styles.root} ${className || ""}`} style={style}>
      {label && <label id={comboId}>{label}</label>}

      <Combobox
        aria-labelledby={comboId}
        multiselect={multiSelect}
        className={styles.combobox}
        onOptionSelect={handleOptionSelect}
        onOpenChange={handleOpenChange}
        selectedOptions={selectedLabels}
        {...rest}
      >
        {loading ? (
          <div className={styles.loaderContainer}>
            <Spinner size="medium" />
          </div>
        ) : (
          options.map((option) => (
            <Option key={option.value}>{option.label}</Option>
          ))
        )}
      </Combobox>
    </div>
  );
};

export default ASCDropDown;
